import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Helmet } from 'react-helmet-async';

const { FiCheckCircle, FiArrowRight, FiCalendar, FiUsers, FiTrendingUp } = FiIcons;

const Announcement = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Acquisition Announcement | Premium SMM Panel</title>
        <meta name="description" content="Premium SMM Panel has been acquired by SMM Panel. Learn about enhanced features, better pricing, and improved services coming your way." />
      </Helmet>

      <div className="pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8 text-center"
          >
            <Link to="/" className="inline-block mb-4">
              <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent hover:from-purple-600 hover:via-blue-600 hover:to-purple-600 transition-all duration-300 tracking-tight">
                Premium SMM Panel
              </span>
            </Link>

            <div className="mb-8">
              <div className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                <span>Acquisition Complete</span>
              </div>
            </div>

            <div className="text-sm text-gray-500 mb-6">
              {currentDate}
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-4">🎉 Exciting News!</h1>
            <p className="text-xl text-gray-600 mb-8">
              Premium SMM Panel has been acquired by SMM Panel
            </p>

            <div className="bg-white rounded-xl shadow-md p-8 mb-8 max-w-3xl mx-auto">
              <div className="prose prose-blue max-w-none">
                <p className="mb-4">
                  We're thrilled to announce that Premium SMM Panel has been acquired by SMM Panel, a leading provider of social media marketing services. This strategic acquisition will allow us to offer you even better services, more competitive pricing, and enhanced features.
                </p>

                <p className="mb-4">
                  <strong>What does this mean for you?</strong>
                </p>

                <ul className="mb-6 list-disc pl-6 space-y-2">
                  <li>Your existing orders will be fulfilled as promised</li>
                  <li>Access to a wider range of services and platforms</li>
                  <li>More competitive pricing and special offers</li>
                  <li>Enhanced customer support and faster delivery times</li>
                  <li>Improved dashboard and user experience</li>
                </ul>

                <p className="mb-4">
                  <strong>Next steps:</strong> All accounts and services will be migrated to the SMM Panel platform. You'll need to create a new account on SMM Panel to continue using our services. We've made this process as seamless as possible.
                </p>

                <p className="mb-6">
                  We want to express our sincere gratitude for your continued support and loyalty. This acquisition marks an exciting new chapter, and we're confident it will bring significant benefits to all our valued customers.
                </p>

                <div className="flex justify-center mb-4">
                  <Link
                    to="/"
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <span>Back to Homepage</span>
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                  </Link>
                </div>

                <p className="text-center text-sm text-gray-500">
                  For any questions, please contact support@smm-panel.com
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-blue-500 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Enhanced Services</h3>
                <p className="text-gray-600 text-sm">Access more platforms and service types with improved quality and results.</p>
              </div>

              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <SafeIcon icon={FiUsers} className="w-8 h-8 text-purple-500 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Better Support</h3>
                <p className="text-gray-600 text-sm">Expanded support team with faster response times and 24/7 availability.</p>
              </div>

              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <SafeIcon icon={FiCalendar} className="w-8 h-8 text-green-500 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Special Offers</h3>
                <p className="text-gray-600 text-sm">Exclusive deals and discounts for migrating customers.</p>
              </div>
            </div>

            <div className="mt-12">
              <Link to="/" className="text-blue-600 hover:text-blue-500">
                Back to homepage
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Announcement;